'use client';

import * as React from 'react';
import {Slot} from '@radix-ui/react-slot';
import {cva, type VariantProps} from 'class-variance-authority';
import {cn} from '@purpose/ui/lib/utils';
import {Spinner} from '../spinner';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'cursor-pointer bg-primary-base text-primary-foreground hover:bg-primary-100',
        destructive:
          'cursor-pointer bg-destructive text-white hover:bg-destructive/90',
        outline:
          'cursor-pointer border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary:
          'cursor-pointer bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost:
          'cursor-pointer hover:bg-secondary-base hover:text-accent-foreground',
        link: 'cursor-pointer text-primary underline-offset-4 hover:underline'
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      isLoading = false,
      asChild = false,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        className={cn(buttonVariants({variant, size, className}))}
        ref={ref}
        disabled={isLoading || props.disabled}
        {...props}
      >
        {isLoading ? <Spinner size="md" /> : children}
      </Comp>
    );
  }
);

Button.displayName = 'Button';

export type ButtonVariant = NonNullable<
  VariantProps<typeof buttonVariants>['variant']
>;
export {Button, buttonVariants};
